
import { useNavigate } from 'react-router-dom'

export interface SharedContentData {
  type: 'post' | 'drama'
  id: string
  title: string
  description: string
  image?: string
  url: string
  meta?: {
    category?: string
    categoryColor?: string
    author?: string
    rating?: number
    year?: number
    episodes?: number
    genre?: string
  }
}

interface SharedContentCardProps {
  data: SharedContentData
  isOwn: boolean
}

const SharedContentCard = ({ data, isOwn }: SharedContentCardProps) => {
  const navigate = useNavigate()

  const handleClick = () => {
    navigate(data.url)
  }

  return (
    <div
      onClick={handleClick}
      className={`rounded-xl overflow-hidden border cursor-pointer transition-all hover:shadow-md max-w-[280px] ${
        isOwn ? 'border-white/20 bg-white/10' : 'border-gray-200 bg-white'
      }`}
    >
      {data.image && (
        <div className="w-full h-32 overflow-hidden">
          <img
            src={data.image}
            alt={data.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      )}
      <div className="p-3">
        <div className="flex items-center gap-2 mb-1.5">
          <span
            className={`text-[10px] font-bold uppercase tracking-wider ${
              data.type === 'drama'
                ? isOwn
                  ? 'text-rose-200'
                  : 'text-rose-500'
                : isOwn
                ? 'text-teal-200'
                : 'text-teal-600'
            }`}
          >
            <i
              className={`${
                data.type === 'drama' ? 'ri-film-line' : 'ri-message-3-line'
              } mr-1`}
            ></i>
            {data.type === 'drama' ? 'Drama' : 'Forum Post'}
          </span>
          {data.meta?.category && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded-full text-white font-medium"
              style={{
                backgroundColor: data.meta.categoryColor || '#6b7280',
              }}
            >
              {data.meta.category}
            </span>
          )}
        </div>

        <h4
          className={`text-sm font-semibold line-clamp-2 mb-1 ${
            isOwn ? 'text-white' : 'text-gray-900'
          }`}
        >
          {data.title}
        </h4>

        <p
          className={`text-xs line-clamp-2 mb-2 ${
            isOwn ? 'text-white/70' : 'text-gray-500'
          }`}
        >
          {data.description}
        </p>

        {/* Meta info */}
        <div
          className={`flex items-center gap-3 text-[11px] ${
            isOwn ? 'text-white/60' : 'text-gray-400'
          }`}
        >
          {data.meta?.author && (
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {data.meta.author}
            </span>
          )}
          {data.meta?.rating && (
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-yellow-400"></i>
              {data.meta.rating}
            </span>
          )}
          {data.meta?.year && <span>{data.meta.year}</span>}
          {data.meta?.episodes && (
            <span>{data.meta.episodes} eps</span>
          )}
        </div>

        <div
          className={`flex items-center gap-1 mt-2 text-[11px] font-medium ${
            isOwn ? 'text-white/80' : 'text-purple-600'
          }`}
        >
          <span>
            View {data.type === 'drama' ? 'Drama' : 'Post'}
          </span>
          <i className="ri-arrow-right-s-line"></i>
        </div>
      </div>
    </div>
  )
}

export default SharedContentCard

// Helper to encode shared content into a message string
export const encodeSharedContent = (content: SharedContentData): string => {
  return `__SHARED_CONTENT__${JSON.stringify(content)}__END_SHARED__`
}

// Helper to decode shared content from a message string
export const decodeSharedContent = (
  message: string
): {
  isShared: boolean
  content?: SharedContentData
  text?: string
} => {
  const match = message.match(/__SHARED_CONTENT__(.+?)__END_SHARED__/)
  if (match) {
    try {
      const content = JSON.parse(match[1]) as SharedContentData
      const remainingText = message.replace(match[0], '').trim()
      return {
        isShared: true,
        content,
        text: remainingText || undefined,
      }
    } catch {
      // If parsing fails, treat as not a shared message
      return { isShared: false }
    }
  }
  return { isShared: false }
}
